import type { EngagementLevel, SurfacedItem } from '@app/core';
import {
  buildQueue,
  checkFading,
  checkWarmingExpiry,
  computeTemperature,
  handleCompletion,
  handleReheat,
} from '@app/core';

import type { ItemRepository } from './repositories.js';
import type { TimeProvider } from './time-provider.js';

export class ItemService {
  constructor(
    private repo: ItemRepository,
    private time: TimeProvider,
  ) {}

  async getSurfacingQueue(engagement: EngagementLevel): Promise<SurfacedItem[]> {
    const items = await this.repo.getActive();
    return buildQueue(items, engagement, this.time.now());
  }

  async complete(id: string): Promise<void> {
    const item = await this.repo.getById(id);
    if (!item) return;
    const updated = handleCompletion(item, this.time.now());
    await this.repo.update(id, updated);
  }

  async snooze(id: string): Promise<void> {
    await this.repo.update(id, { lastInteractedAt: this.time.now() });
  }

  async dismiss(id: string): Promise<void> {
    await this.repo.update(id, {
      status: 'dismissed',
      lastInteractedAt: this.time.now(),
    });
  }

  async reheat(id: string): Promise<void> {
    const item = await this.repo.getById(id);
    if (!item) return;
    const updated = handleReheat(item, this.time.now());
    await this.repo.update(id, updated);
  }

  async checkPhaseTransitions(): Promise<void> {
    const items = await this.repo.getActive();
    const now = this.time.now();
    for (const item of items) {
      let updated = checkWarmingExpiry(item, now);
      const temp = computeTemperature(updated.curve, updated.createdAt, now);
      updated = checkFading(updated, temp);
      if (updated !== item) {
        await this.repo.update(item.id, updated);
      }
    }
  }

  async markSurfaced(id: string): Promise<void> {
    await this.repo.update(id, { lastSurfacedAt: this.time.now() });
  }
}
